import React, { useEffect, useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Grid, Paper, FormControl, InputLabel, Select, MenuItem, Typography, Box } from "@material-ui/core";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";

const useStyles = makeStyles(theme => ({
  mainContainer: {
    flex: 1,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    height: "calc(100vh - 64px)",
    overflow: "hidden"
  },
  paper: {
    padding: theme.spacing(2),
    height: "100%",
    backgroundColor: theme.palette.background.paper
  },
  panel: {
    height: 380
  },
  select: {
    minWidth: 280
  },
  legendContainer: {
    marginTop: theme.spacing(2),
    maxHeight: 140,
    overflowY: "auto",
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1)
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`
  },
  legendDot: {
    width: 12,
    height: 12,
    borderRadius: 2
  }
}));

const FlowBuilderAnalytics = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [flows, setFlows] = useState([]);
  const [flowId, setFlowId] = useState("");
  const [analytics, setAnalytics] = useState({ nodes: [], metrics: { countsByNode: {}, waitingByNode: {}, totalActive: 0 }, flow: { name: "" } });

  useEffect(() => {
    const loadFlows = async () => {
      const { data } = await api.get("/flowbuilder");
      setFlows(data.flows || []);
      if (data.flows && data.flows.length > 0 && !flowId) {
        setFlowId(String(data.flows[0].id));
      }
    };
    loadFlows();
  }, []);

  useEffect(() => {
    const loadAnalytics = async () => {
      if (!flowId) return;
      const { data } = await api.get(`/flowbuilder/analytics/${flowId}`);
      setAnalytics(data);
    };
    loadAnalytics();
  }, [flowId]);

  const chartData = (analytics.nodes || []).map(n => ({
    name: n.label || n.type,
    id: n.id,
    waiting: analytics.metrics.waitingByNode?.[n.id] || 0
  }));

  const colors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.error.main,
    "#00a884",
    "#8e44ad",
    "#2ecc71",
    "#f39c12"
  ];

  return (
    <MainContainer className={classes.mainContainer}>
      <MainHeader>
        <Title>Flowbuilder</Title>
      </MainHeader>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper className={classes.paper} elevation={0}>
            <FormControl variant="outlined" className={classes.select}>
              <InputLabel id="flow-select-label">Fluxo</InputLabel>
              <Select
                labelId="flow-select-label"
                value={flowId}
                onChange={e => setFlowId(String(e.target.value))}
                label="Fluxo"
              >
                {flows.map(f => (
                  <MenuItem key={f.id} value={String(f.id)}>{f.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box mt={2}>
              <Typography variant="subtitle2" color="textSecondary">
                {analytics.flow?.name ? `Fluxo: ${analytics.flow.name}` : ""}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Tickets ativos no fluxo: {analytics.metrics?.totalActive || 0}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper className={classes.paper} elevation={0}>
            <Typography variant="h6">Distribuição atual por etapa</Typography>
            <div className={classes.panel}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={chartData} margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
                  <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fill: theme.palette.text.primary }} tickFormatter={(v) => Math.round(v)} />
                  <YAxis type="category" dataKey="name" tick={false} tickLine={false} axisLine={false} width={0} />
                  <Tooltip />
                  <Bar dataKey="waiting" name="Fluxo parado">
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <Box className={classes.legendContainer}>
              {chartData.map((entry, index) => (
                <Box key={`legend-${entry.id}-${index}`} className={classes.legendItem}>
                  <span className={classes.legendDot} style={{ backgroundColor: colors[index % colors.length] }} />
                  <Typography variant="caption">{entry.name}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </MainContainer>
  );
};

export default FlowBuilderAnalytics;
